<template>
  <div :class="['input-box', { 'is-mobile': isMobile }]">
    <textarea
      ref="textareaRef"
      v-model="message"
      :disabled="isDisabled"
      @input="autoGrow"
      @keydown.enter.prevent="sendMessage"
      @focus="handleFocus"
      rows="1"
    />

    <div v-if="showSendButton" ref="sendButtonRef" class="send-btn" @click="sendMessage">Send</div>
  </div>
</template>

<script setup lang="ts">
import { isMobile } from '@/core/helpers/ui.helper';
import { useChatStore } from '@/stores/chat.store';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

export interface InputBoxProps {
  onFocus?: (e?: Event) => void;
  isDisabled?: boolean;
}

const props = defineProps<InputBoxProps>();

const message = ref<string>('');

const textareaRef = ref<HTMLTextAreaElement>();
const sendButtonRef = ref<HTMLDivElement>();

const chatStore = useChatStore();
const { isMessageSubmitting } = storeToRefs(chatStore);

// Computed

const isMessageEmpty = computed(() => message.value === '');
const showSendButton = computed(() => {
  return !isMessageEmpty.value && !isMessageSubmitting.value;
});

// Drag Observers

const preventDrag = (e: TouchEvent) => {
  e.preventDefault();
};

onMounted(() => {
  if (!textareaRef.value) {
    return;
  }
});

watch(sendButtonRef, (sendButton) => {
  if (!sendButton) {
    return;
  }

  sendButton.addEventListener('touchstart', sendMessage);
  sendButton.addEventListener('touchmove', preventDrag, { passive: false });
});

// Handlers

const handleFocus = (e: Event) => {
  if (props.onFocus) {
    props.onFocus(e);
  }
};

const autoGrow = () => {
  const el = textareaRef.value;
  if (!el) return;

  el.style.height = 'auto';
  el.style.height = String(Number(el.scrollHeight) + 2) + 'px';
};

const sendMessage = async (e: Event) => {
  e.preventDefault();
  e.stopPropagation();

  if (!message.value.trim() || isMessageSubmitting.value === true) return;

  const text = message.value;
  message.value = '';
  nextTick(() => {
    autoGrow();
  });

  await chatStore.sendMessage(text);
};

// Expose

const focus = (e?: Event) => {
  const el = textareaRef.value;
  if (!el) return;

  e?.preventDefault();
  e?.stopPropagation();

  el.focus();
};

defineExpose({ focus });
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.input-box {
  pointer-events: none;

  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
}

.input-box.is-mobile {
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
}

textarea {
  pointer-events: auto;

  box-sizing: border-box;
  appearance: none;
  width: 100%;
  resize: none;
  overflow: hidden;
  background-color: var(--color-dark-700);
  color: var(--color-white);
  border: 1px solid var(--color-white-10);
  border-radius: 22.5px;
  padding: 11px 64px 13px 18px;
  font-size: 16px;
  line-height: 21px;
  font-weight: 300;
}

textarea:not([disabled]) {
  @include hover {
    border-color: var(--color-white-30);
  }

  &:focus {
    border-color: var(--color-pink);
  }
}

.send-btn {
  pointer-events: auto;

  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(calc(-50% - 1px));
  // right: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 47px;
  color: var(--color-pink);
  cursor: pointer;
  padding: 0 18px;

  @include hover {
    opacity: 0.75;
  }
}
</style>
